import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import { NgPrincipleModule } from '@principle-theorem/ng-principle';
import {
  NgPrincipleSharedModule,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  ButtonsContainerComponent,
  PipesModule,
  ProfileImageService,
  SelectionListStore,
} from '@principle-theorem/ng-shared';
import {
  ApiScope,
  convertApiScopeToDescription,
  IPartnerApp,
  IPractice,
  IUser,
  PartnerCollection,
} from '@principle-theorem/principle-core/interfaces';
import {
  collectionGroupQuery,
  filterUndefined,
  firstResult$,
  getEnumValues,
  isSameRef,
  multiSortBy$,
  nameSorter,
  serialise,
  shareReplayCold,
  snapshot,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { Observable, of, ReplaySubject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { OAuthService } from './oauth.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'pr-oauth-consent',
  templateUrl: './oauth-consent.component.html',
  styleUrl: './oauth-consent.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgPrincipleModule,
    NgMaterialModule,
    NgPrincipleSharedModule,
    ButtonsContainerComponent,
    PipesModule,
  ],
  providers: [OAuthService, SelectionListStore],
})
export class OAuthConsentComponent {
  user$: Observable<IUser>;
  clientId$ = new ReplaySubject<string>(1);
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input({ alias: 'redirect_uri' }) redirectUri: string;
  app$: Observable<WithRef<IPartnerApp> | undefined>;
  appIconUrl$: Observable<string | undefined>;
  appName$: Observable<string | undefined>;
  scopes: string[];
  scopeDescriptions: string[];
  practices$: Observable<WithRef<IPractice>[]>;
  @Input() state: string;

  @Input({
    // eslint-disable-next-line @angular-eslint/no-input-rename
    alias: 'scope',
    transform: (rawScopes: string) => {
      // eslint-disable-next-line @angular-eslint/no-input-rename
      const scopes = String(rawScopes).split(/,|\s/);
      return scopes.filter((scope) =>
        getEnumValues(ApiScope).includes(scope as ApiScope)
      );
    },
  })
  set scope(scopes: ApiScope[]) {
    this.scopes = scopes;
    this.scopeDescriptions = scopes.map((scope) =>
      convertApiScopeToDescription(scope)
    );
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input({ alias: 'client_id' })
  set clientId(clientId: string) {
    if (clientId) {
      this.clientId$.next(clientId);
    }
  }

  constructor(
    private _oauth: OAuthService,
    organisation: OrganisationService,
    profileImage: ProfileImageService,
    public selectionList: SelectionListStore<WithRef<IPractice>>,
    private _location: Location,
    private _snackBar: MatSnackBar
  ) {
    this.user$ = organisation.user$.pipe(filterUndefined());

    this.practices$ = organisation.userPractices$.pipe(
      multiSortBy$(nameSorter()),
      shareReplayCold()
    );

    this.selectionList.setCompareFn(isSameRef);

    this.practices$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((practices) => {
        this.selectionList.loadOptions(of(practices));
        this.selectionList.toggleAllSelected();
      });

    this.app$ = this.clientId$.pipe(
      switchMap((clientId) =>
        firstResult$(
          collectionGroupQuery<IPartnerApp>(PartnerCollection.Apps),
          where('oauth.clientId', '==', clientId)
        )
      ),
      shareReplayCold()
    );

    this.appIconUrl$ = this.app$.pipe(
      switchMap((app) => {
        if (!app?.appIconUrl) {
          return of(undefined);
        }
        return profileImage.getUserProfileImage$({
          name: '',
          profileImageURL: app.appIconUrl,
        });
      })
    );

    this.appName$ = this.app$.pipe(map((app) => app?.name ?? undefined));
  }

  async allow(): Promise<void> {
    const clientId = await snapshot(this.clientId$);
    const practiceRefs = await snapshot(
      this.selectionList.selected$.pipe(
        map((practices) => practices.map((practice) => practice.ref))
      )
    );
    const { redirectUri, code } = await this._oauth.confirmConsent(
      practiceRefs,
      clientId,
      this.redirectUri,
      this.scopes,
      this.state
    );

    if (!redirectUri || !code) {
      // eslint-disable-next-line no-console
      console.error(
        'There was an issue connecting with the Partner',
        serialise({
          practiceRefs,
          clientId,
          redirectUri: this.redirectUri,
          scopes: this.scopes,
          state: this.state,
        })
      );
      this._snackBar.open(
        'There was an issue connecting with the Partner. Please try again.',
        'Close',
        {
          duration: 5000,
        }
      );
      return;
    }

    const url = new URL(redirectUri);
    url.searchParams.set('code', code);
    url.searchParams.set('state', this.state);
    window.location.href = url.toString();
  }

  deny(): void {
    this._location.back();
  }
}
